import {useCallback} from 'react';
import {Tokens} from '../../modules/auth/common/Types';
import {useAuth} from '../../modules/auth/providers/AuthProvider';
import {useApiUrl} from '../providers/ConfigProvider';
import {addAuthHeader, isUnauthorizedHttpError, postHttp} from './ApiClient';

interface RefreshProps {
	refreshUrl: string;
	error: unknown;
	refreshToken: string;
	logout: () => void;
	updateTokens: (tokens: Tokens) => void;
}

class TokenRefresher {
	private static ongoingRequest?: Promise<Tokens> = undefined;

	private static getRequest(refreshUrl: string, refreshToken: string) {
		if (TokenRefresher.ongoingRequest === undefined) {
			TokenRefresher.ongoingRequest = postHttp<Tokens>(refreshUrl, {}, addAuthHeader({}, refreshToken));
		}

		return TokenRefresher.ongoingRequest;
	}

	private static removeRequest() {
		TokenRefresher.ongoingRequest = undefined;
	}

	static async refresh({refreshUrl, error, refreshToken, logout, updateTokens}: RefreshProps) {
		if (!isUnauthorizedHttpError(error)) throw error;

		try {
			const request = TokenRefresher.getRequest(refreshUrl, refreshToken);
			const tokens = await request;
			updateTokens(tokens);
			return tokens;
		} catch (e) {
			if (!isUnauthorizedHttpError(e)) throw e;
			logout();
			return null;
		} finally {
			TokenRefresher.removeRequest();
		}
	}
}

const useTokenRefresh = () => {
	const {currentUser, logout, updateTokens} = useAuth();
	const refreshUrl = useApiUrl('enterpriser/auth/refresh-token');
	const refreshToken = currentUser?.refresh_token ?? '';
	return useCallback(
		async (error: unknown) => {
			return TokenRefresher.refresh({refreshUrl, error, refreshToken, logout, updateTokens});
		},
		[refreshToken, logout, refreshUrl, updateTokens],
	);
};

export const useCallWithRefreshedToken = <T>() => {
	const refresh = useTokenRefresh();
	return useCallback(
		async (normalCall: () => Promise<T>, refreshedCall: (newToken: string) => Promise<T>) => {
			try {
				return await normalCall();
			} catch (error) {
				const newTokens = await refresh(error);
				if (newTokens) return await refreshedCall(newTokens.access_token);
				throw error;
			}
		},
		[refresh],
	);
};
